import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SttFormSelect } from '../all';

type Props = {
    grid?: boolean;
    name: string;
    caption: string;
    defVal: string;
    options?: string[];
    withMultiYear?: boolean;
    onChange: (event: { name: string | undefined; value: unknown }) => void;
    cleanable?: boolean;
    required?: boolean;
    size?: 'small' | 'medium';
    variant?: 'outlined' | 'filled' | 'standard';
    [key: string]: unknown;
};

/**
 * SttSelectFixedInterval: present a list of pre-defined time intervals (as they are defined in the back end). Used for fees and such.
 * bool grid: wrap in a Grid item
 * string name: field name, for forms and such
 * string caption
 * string defVal: currently selected value
 * <string>array options: enabled options. Must be a subset of the OPTIONS defined below, for example: ['1 DAY', '2 DAY', '1 WEEK'...]
 * bool withMultiYear: also offer 2, 3 and 4 years (only bono.expiration accepts them)
 * ...rest of the props, typically xs, md, etc.
 */
export function SttSelectFixedInterval({
    grid,
    name,
    caption,
    defVal,
    options,
    withMultiYear = false,
    onChange,
    cleanable = false,
    required = false,
    size: _size = 'small',
    variant: _variant = 'outlined',
    ...rest
}: Props): React.ReactElement {
    const { t } = useTranslation();

    // Available options to pick from.
    const OPTIONS = [
        {
            name: 'NO PERIOD',
            label: t('0000-00-00 00:00:00'),
            value: '0000-00-00 00:00:00',
        },
        {
            name: '1 DAY',
            label: t('0000-00-01 00:00:00'),
            value: '0000-00-01 00:00:00',
        },
        {
            name: '2 DAY',
            label: t('0000-00-02 00:00:00'),
            value: '0000-00-02 00:00:00',
        },
        {
            name: '3 DAY',
            label: t('0000-00-03 00:00:00'),
            value: '0000-00-03 00:00:00',
        },
        {
            name: '4 DAY',
            label: t('0000-00-04 00:00:00'),
            value: '0000-00-04 00:00:00',
        },
        {
            name: '5 DAY',
            label: t('0000-00-05 00:00:00'),
            value: '0000-00-05 00:00:00',
        },
        {
            name: '6 DAY',
            label: t('0000-00-06 00:00:00'),
            value: '0000-00-06 00:00:00',
        },
        {
            name: '1 WEEK',
            label: t('0000-00-07 00:00:00'),
            value: '0000-00-07 00:00:00',
        },
        {
            name: '8 DAY',
            label: t('0000-00-08 00:00:00'),
            value: '0000-00-08 00:00:00',
        },
        {
            name: '9 DAY',
            label: t('0000-00-09 00:00:00'),
            value: '0000-00-09 00:00:00',
        },
        {
            name: '10 DAY',
            label: t('0000-00-10 00:00:00'),
            value: '0000-00-10 00:00:00',
        },
        {
            name: '11 DAY',
            label: t('0000-00-11 00:00:00'),
            value: '0000-00-11 00:00:00',
        },
        {
            name: '12 DAY',
            label: t('0000-00-12 00:00:00'),
            value: '0000-00-12 00:00:00',
        },
        {
            name: '13 DAY',
            label: t('0000-00-13 00:00:00'),
            value: '0000-00-13 00:00:00',
        },
        {
            name: '2 WEEK',
            label: t('0000-00-14 00:00:00'),
            value: '0000-00-14 00:00:00',
        },
        {
            name: '3 WEEK',
            label: t('0000-00-21 00:00:00'),
            value: '0000-00-21 00:00:00',
        },
        {
            name: 'FORTNIGHT',
            label: t('0000-00-15 00:00:00'),
            value: '0000-00-15 00:00:00',
        },
        {
            name: '28 DAY',
            label: t('0000-00-28 00:00:00'),
            value: '0000-00-28 00:00:00',
        },
        {
            name: '1 MONTH',
            label: t('0000-01-00 00:00:00'),
            value: '0000-01-00 00:00:00',
        },
        {
            name: '2 MONTH',
            label: t('0000-02-00 00:00:00'),
            value: '0000-02-00 00:00:00',
        },
        {
            name: '3 MONTH',
            label: t('0000-03-00 00:00:00'),
            value: '0000-03-00 00:00:00',
        },
        {
            name: '4 MONTH',
            label: t('0000-04-00 00:00:00'),
            value: '0000-04-00 00:00:00',
        },
        {
            name: '6 MONTH',
            label: t('0000-06-00 00:00:00'),
            value: '0000-06-00 00:00:00',
        },
        {
            name: '1 YEAR',
            label: t('0001-00-00 00:00:00'),
            value: '0001-00-00 00:00:00',
        },
    ];

    const MULTI_YEAR_OPTIONS = [
        {
            name: '2 YEAR',
            label: t('0002-00-00 00:00:00'),
            value: '0002-00-00 00:00:00',
        },
        {
            name: '3 YEAR',
            label: t('0003-00-00 00:00:00'),
            value: '0003-00-00 00:00:00',
        },
        {
            name: '4 YEAR',
            label: t('0004-00-00 00:00:00'),
            value: '0004-00-00 00:00:00',
        },
    ];

    const availableOptions = withMultiYear
        ? [...OPTIONS, ...MULTI_YEAR_OPTIONS]
        : OPTIONS;

    let usedOptions;
    if (options && options.length > 0) {
        // Add options explicitly included
        usedOptions = availableOptions.filter((opt: any) =>
            options.includes(opt.name),
        );

        // Check for explicitly excluded options instead
        if (usedOptions.length === 0) {
            usedOptions = availableOptions.filter(
                (opt: any) => !options.includes(`!${opt.name}`),
            );
        }
    } else {
        usedOptions = availableOptions;
    }

    const content = (
        <SttFormSelect
            name={name}
            caption={caption}
            defVal={defVal}
            options={usedOptions}
            onChange={onChange as any}
            cleanable={cleanable}
            required={required}
        />
    );

    return grid ? <Grid {...rest}>{content}</Grid> : content;
}
